import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as mentorApi from '../../api/mentorApi';

const MIN_MEMBERS = 2;
const MAX_MEMBERS = 5;
const MISSIONS = [1, 2, 3, 4, 5];
const EMPTY_CHAIR_BY_MISSION = { 1: '', 2: '', 3: '', 4: '', 5: '' };

// Generic error copy only - never surface raw backend text (see form-error
// clearing convention used elsewhere in this app).
const GENERIC_SUBMIT_ERROR = "Couldn't form the squad — try again.";

export default function EsgSquadFormation({ onError, onSuccess }) {
  const [levels, setLevels] = useState([]);
  const [levelsLoading, setLevelsLoading] = useState(true);
  const [selectedLevelId, setSelectedLevelId] = useState('');

  const [availableStudents, setAvailableStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [schoolFilter, setSchoolFilter] = useState('');
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  // Mission number -> student id string ('' = unset). All 5 are optional
  // here - any left blank can be set later by an auditor via the
  // per-mission chair-rotation update (mission 1 there can never be
  // changed once it IS recorded, whether that happens here or later).
  const [chairByMission, setChairByMission] = useState(EMPTY_CHAIR_BY_MISSION);
  // Handles Introduction Session admin duties (attendance, chair
  // allocation, next session date/time, squad introductions, squad name,
  // ground rules, session minutes) - required, one of the selected members.
  const [adminLeadId, setAdminLeadId] = useState('');

  const [spocs, setSpocs] = useState([]);
  const [auditors, setAuditors] = useState([]);
  const [spocId, setSpocId] = useState('');
  const [auditorAssignments, setAuditorAssignments] = useState([]); // [{auditorId, isStandby}]

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const clearFormError = useCallback(() => setFormError(''), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLevelsLoading(true);
      try {
        const data = await mentorApi.getEsgLevels();
        if (!cancelled) setLevels(data);
      } catch (err) {
        if (!cancelled) onError?.(err?.message || 'Failed to load ESG levels');
      } finally {
        if (!cancelled) setLevelsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [onError]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [spocRows, auditorRows] = await Promise.all([
          mentorApi.getUsersByRole('spoc'),
          mentorApi.getUsersByRole('auditor'),
        ]);
        if (!cancelled) {
          setSpocs(spocRows);
          setAuditors(auditorRows);
        }
      } catch (err) {
        if (!cancelled) onError?.(err?.message || 'Failed to load SPOC/auditor list');
      }
    })();
    return () => { cancelled = true; };
  }, [onError]);

  const loadAvailableStudents = useCallback(async (levelId) => {
    if (!levelId) {
      setAvailableStudents([]);
      return;
    }
    setStudentsLoading(true);
    try {
      const students = await mentorApi.getEsgAvailableStudents(levelId);
      setAvailableStudents(students);
    } catch (err) {
      onError?.(err?.message || 'Failed to load available students');
    } finally {
      setStudentsLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    setSelectedStudentIds([]);
    setChairByMission(EMPTY_CHAIR_BY_MISSION);
    setAdminLeadId('');
    setSpocId('');
    setAuditorAssignments([]);
    setSchoolFilter('');
    clearFormError();
    loadAvailableStudents(selectedLevelId);
  }, [selectedLevelId, loadAvailableStudents, clearFormError]);

  const schoolOptions = useMemo(() => {
    const names = new Set(availableStudents.map((s) => s.school_name).filter(Boolean));
    return Array.from(names).sort();
  }, [availableStudents]);

  const filteredStudents = useMemo(() => {
    const q = studentSearch.trim().toLowerCase();
    return availableStudents.filter((s) => {
      if (schoolFilter && s.school_name !== schoolFilter) return false;
      if (!q) return true;
      return String(s.name || '').toLowerCase().includes(q) ||
        String(s.grade || '').toLowerCase().includes(q) ||
        String(s.school_name || '').toLowerCase().includes(q);
    });
  }, [availableStudents, studentSearch, schoolFilter]);

  const toggleStudent = useCallback((studentId) => {
    clearFormError();
    setSelectedStudentIds((prev) => {
      if (prev.includes(studentId)) {
        // Deselecting a student clears any mission chair pick that pointed
        // to them - they're no longer a valid choice (a chair must be one
        // of the selected students).
        setChairByMission((prevChairs) => {
          const next = { ...prevChairs };
          for (const mission of MISSIONS) {
            if (next[mission] === String(studentId)) next[mission] = '';
          }
          return next;
        });
        setAdminLeadId((prevLead) => (prevLead === String(studentId) ? '' : prevLead));
        return prev.filter((id) => id !== studentId);
      }
      if (prev.length >= MAX_MEMBERS) return prev;
      return [...prev, studentId];
    });
  }, [clearFormError]);

  const toggleAuditorSelect = useCallback((auditorId) => {
    clearFormError();
    setAuditorAssignments((prev) => {
      const exists = prev.find((a) => a.auditorId === auditorId);
      if (exists) return prev.filter((a) => a.auditorId !== auditorId);
      return [...prev, { auditorId, isStandby: false }];
    });
  }, [clearFormError]);

  const toggleAuditorStandby = useCallback((auditorId) => {
    clearFormError();
    setAuditorAssignments((prev) =>
      prev.map((a) => (a.auditorId === auditorId ? { ...a, isStandby: !a.isStandby } : a))
    );
  }, [clearFormError]);

  const handleChairChange = useCallback((mission, value, nameById) => {
    clearFormError();
    if (value) {
      const otherMission = MISSIONS.find((m) => m !== mission && chairByMission[m] === value);
      if (otherMission) {
        const name = nameById.get(Number(value)) || 'This student';
        const confirmed = window.confirm(
          `${name} is already Chair for Mission ${otherMission}. Assign them as Chair for Mission ${mission} too?`
        );
        if (!confirmed) return;
      }
    }
    setChairByMission((prev) => ({ ...prev, [mission]: value }));
  }, [clearFormError, chairByMission]);

  const chairOptions = useMemo(
    () => availableStudents.filter((s) => selectedStudentIds.includes(s.id)),
    [availableStudents, selectedStudentIds]
  );

  const chairOptionsById = useMemo(
    () => new Map(chairOptions.map((s) => [s.id, s.name])),
    [chairOptions]
  );

  const canSubmit = selectedLevelId
    && selectedStudentIds.length >= MIN_MEMBERS
    && selectedStudentIds.length <= MAX_MEMBERS
    && !!adminLeadId
    && !submitting;

  const handleSubmit = useCallback(async () => {
    clearFormError();
    if (!canSubmit) return;

    setSubmitting(true);
    try {
      const chairAssignments = MISSIONS
        .filter((mission) => chairByMission[mission])
        .map((mission) => ({ mission_number: mission, student_id: Number(chairByMission[mission]) }));

      const squad = await mentorApi.createEsgSquad({
        level_id: Number(selectedLevelId),
        student_ids: selectedStudentIds.map(Number),
        admin_lead_student_id: Number(adminLeadId),
        chair_assignments: chairAssignments,
        spoc_id: spocId ? Number(spocId) : null,
        auditor_assignments: auditorAssignments.map((a) => ({
          user_id: Number(a.auditorId),
          is_standby: a.isStandby,
        })),
      });

      setSelectedStudentIds([]);
      setChairByMission(EMPTY_CHAIR_BY_MISSION);
      setAdminLeadId('');
      setSpocId('');
      setAuditorAssignments([]);
      onSuccess?.(`Squad "${squad.name}" formed.`);
      // Re-fetch available students - the ones just squadded should drop off the list.
      loadAvailableStudents(selectedLevelId);
      // Open the new squad's page.
      if (squad?.id) window.location.hash = `#/esg/squad/${squad.id}`;
    } catch (err) {
      // 4xx errors from this endpoint are our own controlled validation
      // messages (e.g. "not enough students", "account not synced yet") -
      // safe and meant to be read by the user. Only unexpected/server
      // errors fall back to generic copy.
      const isKnownValidationError = err?.status && err.status < 500 && err?.message;
      const displayMessage = isKnownValidationError ? err.message : GENERIC_SUBMIT_ERROR;
      setFormError(displayMessage);
      onError?.(displayMessage);
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, clearFormError, selectedLevelId, selectedStudentIds, adminLeadId, chairByMission, spocId, auditorAssignments, loadAvailableStudents, onSuccess, onError]);

  return (
    <div className="wizard-card animate-fade-in">
      <div className="dashboard-title-row">
        <div>
          <h1 className="dashboard-title">New Squad Creation</h1>
          <p className="dashboard-subtitle">Select a level, choose 2–5 enrolled students, and form a squad.</p>
        </div>
      </div>

      <div className="wizard-form-group">
        <label htmlFor="esgLevelSelect">Level</label>
        <select
          id="esgLevelSelect"
          className="wizard-select"
          value={selectedLevelId}
          onChange={(e) => setSelectedLevelId(e.target.value)}
          disabled={levelsLoading}
        >
          <option value="" disabled>{levelsLoading ? 'Loading levels…' : '-- Choose Level --'}</option>
          {levels.map((l) => (
            <option key={l.id} value={l.id}>{l.name}</option>
          ))}
        </select>
      </div>

      {selectedLevelId && (
        <>
          <div className="wizard-form-group">
            <div className="esg-squad-formation__div-1">
              <label className="esg-squad-formation__label-2">Available Students</label>
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: 700,
                  padding: '3px 10px',
                  borderRadius: '999px',
                  background: selectedStudentIds.length >= MIN_MEMBERS ? 'rgba(1, 71, 162, 0.1)' : 'rgba(248, 159, 9, 0.15)',
                  color: selectedStudentIds.length >= MIN_MEMBERS ? 'var(--primary-navy)' : '#a86400',
                }}
              >
                {selectedStudentIds.length} of {MAX_MEMBERS} selected
              </span>
            </div>
            <div className="esg-squad-formation__div-3">
              <input
                type="text"
                className="wizard-search-input esg-squad-formation__input-4"
                placeholder="🔍 Search available students by name, grade, or school..."
                value={studentSearch}
                onChange={(e) => setStudentSearch(e.target.value)}
              />
              {schoolOptions.length > 1 && (
                <select
                  className="wizard-select esg-squad-formation__select-5"
                  value={schoolFilter}
                  onChange={(e) => setSchoolFilter(e.target.value)}
                >
                  <option value="">All Schools</option>
                  {schoolOptions.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              )}
            </div>
            <div className="wizard-checklist-scrollbox esg-squad-formation__div-6">
              {studentsLoading ? (
                <div className="transfer-empty-state">Loading students…</div>
              ) : filteredStudents.length === 0 ? (
                <div className="transfer-empty-state">No available students found for this level.</div>
              ) : (
                filteredStudents.map((s) => {
                  const isSelected = selectedStudentIds.includes(s.id);
                  const isDisabled = !isSelected && selectedStudentIds.length >= MAX_MEMBERS;
                  return (
                    <div
                      key={s.id}
                      className="checklist-row-item"
                      style={isDisabled ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
                      onClick={() => { if (!isDisabled) toggleStudent(s.id); }}
                    >
                      <input type="checkbox" checked={isSelected} disabled={isDisabled} onChange={() => {}} />
                      <span className="checklist-row-label">
                        {s.name}
                        {s.school_name && (
                          <span className="esg-squad-formation__span-7"> — {s.school_name}</span>
                        )}
                      </span>
                      {s.grade && <span className="transfer-row-badge">{s.grade}</span>}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="wizard-form-group">
            <label htmlFor="esgAdminLeadSelect">Squad Admin Lead (required)</label>
            <p className="esg-squad-formation__p-8">
              Handles Introduction Session admin duties: attendance, chair allocation, next session
              date &amp; time, squad introductions, squad name, ground rules, and session minutes.
            </p>
            <select
              id="esgAdminLeadSelect"
              className="wizard-select"
              value={adminLeadId}
              onChange={(e) => { clearFormError(); setAdminLeadId(e.target.value); }}
              disabled={chairOptions.length === 0}
            >
              <option value="">
                {chairOptions.length === 0 ? '-- Select students first --' : '-- Select Squad Admin Lead --'}
              </option>
              {chairOptions.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          <div className="wizard-form-group">
            <div className="esg-squad-formation__div-1">
              <label className="esg-squad-formation__label-2">Assign Auditors (optional)</label>
              {auditorAssignments.length > 0 && (
                <span
                  className="esg-squad-formation__span-9"
                >
                  {auditorAssignments.length} auditor{auditorAssignments.length !== 1 ? 's' : ''} selected
                </span>
              )}
            </div>
            <div className="auditor-wizard-grid esg-squad-formation__div-10">
              {auditors.length === 0 ? (
                <div className="transfer-empty-state">No auditors available yet.</div>
              ) : (
                auditors.map((aud) => {
                  const assignment = auditorAssignments.find((a) => a.auditorId === aud.id);
                  const isSelected = !!assignment;
                  return (
                    <div key={aud.id} className={`auditor-wizard-row ${isSelected ? 'selected' : ''}`}>
                      <div className="auditor-wizard-left" onClick={() => toggleAuditorSelect(aud.id)}>
                        <input type="checkbox" checked={isSelected} onChange={() => {}} />
                        <span className="auditor-wizard-name">{aud.name}</span>
                      </div>
                      {isSelected && (
                        <div className="auditor-wizard-right">
                          <div className="standby-toggle-container">
                            <span>STANDBY</span>
                            <label className="standby-switch">
                              <input
                                type="checkbox"
                                checked={assignment.isStandby}
                                onChange={() => toggleAuditorStandby(aud.id)}
                              />
                              <span className="standby-slider"></span>
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="wizard-form-group">
            <label htmlFor="esgSpocSelect">Assign SPOC (optional)</label>
            <select
              id="esgSpocSelect"
              className="wizard-select"
              value={spocId}
              onChange={(e) => { clearFormError(); setSpocId(e.target.value); }}
            >
              <option value="">-- No SPOC --</option>
              {spocs.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          <div className="wizard-form-group">
            <label>Chair Allocation (all optional)</label>
            <p className="esg-squad-formation__p-8">
              Chair rotates every mission. Any mission left blank here can be set later by an auditor
              — except Mission 1, which can never be changed once it's recorded (here or later).
            </p>
            <div className="esg-chair-mission-grid">
              {MISSIONS.map((mission) => (
                <div key={mission}>
                  <label htmlFor={`esgChairSelect_${mission}`} className="esg-squad-formation__label-11">
                    {`Mission ${mission} Chair (optional)`}
                  </label>
                  <select
                    id={`esgChairSelect_${mission}`}
                    className="wizard-select"
                    value={chairByMission[mission]}
                    onChange={(e) => handleChairChange(mission, e.target.value, chairOptionsById)}
                    disabled={chairOptions.length === 0}
                  >
                    <option value="">
                      {chairOptions.length === 0 ? '-- Select students first --' : '-- No Chair Yet --'}
                    </option>
                    {chairOptions.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          {formError && (
            <div className="transfer-empty-state esg-squad-formation__div-12">
              {formError}
            </div>
          )}

          <div className="wizard-footer-buttons esg-squad-formation__div-13">
            <button
              className="btn-wizard-next"
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              {submitting ? 'Forming Squad…' : 'Form Squad'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
